/**
 * Shapes returned by the control plane.
 *
 * These mirror the Rust types in `crates/xinfer-api`. Nothing in either
 * language enforces that, so `crates/xinfer-api/tests/openapi.rs` asserts that
 * every field named here is actually produced by a handler — a renamed field
 * fails that test rather than becoming `undefined` in a chart.
 */

/** Never `secret_hash`: the value the gate authenticates with stays server-side. */
export interface ApiKey {
  key_id: string;
  account_id: string;
  /** Last four characters, so a human can tell two keys apart. */
  hint: string;
  revoked: boolean;
  created_at: string;
  last_used_at: string | null;
}

/** The plaintext, returned exactly once and never again. */
export interface MintedKey {
  key: string;
  key_id: string;
  hint: string;
  warning: string;
}

export type AccountKind = 'personal' | 'team';
export type AccountStatus = 'active' | 'suspended' | 'closed';
export type Role = 'owner' | 'admin' | 'member' | 'readonly';

export interface Account {
  id: string;
  name: string;
  kind: AccountKind;
  status: AccountStatus;
  plan: string;
  role: Role;
}

export interface CurrentUser {
  user_id: string;
  /**
   * Display name. The only label available for a user who signed in with a
   * provider that has no email, such as WeChat or QQ.
   */
  name: string | null;
  /**
   * Contact address, not an identity. Null for providers that never supply
   * one -- render `name` in preference to this.
   */
  email: string | null;
  is_staff: boolean;
}

export interface Me {
  user: CurrentUser;
  accounts: Account[];
  /**
   * Which account the portal should show. Present so the SPA never has to
   * reason about *which* account it is in until there is more than one.
   */
  default_account_id: string | null;
}

/**
 * Usage, in the unit the service actually meters in.
 *
 * `peak_kv_tokens` is a high-water mark, not a sum: two concurrent 25k
 * sessions are not one 50k session.
 */
export interface UsageTotals {
  requests: number;
  input_tokens: number;
  output_tokens: number;
  kv_token_seconds: number;
  peak_kv_tokens: number;
}

export interface ApiErrorBody {
  error: { message: string; type: string; code?: string };
}

/** An error carrying the status, so callers can distinguish 401 from 500. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly type: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Not signed in — the caller should show the login page, not an error. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

const CSRF_COOKIE = 'xinfer-csrf';
const CSRF_HEADER = 'x-xinfer-csrf';

function csrfToken(): string {
  const match = document.cookie
    .split(';')
    .map((part) => part.trim().split('='))
    .find(([name]) => name === CSRF_COOKIE);
  return match?.[1] ?? '';
}

/**
 * The control-plane client.
 *
 * Same-origin by default, which is what lets the session cookie be
 * `SameSite=Strict` and removes CORS entirely. `credentials: 'same-origin'`
 * rather than `include`, so a misconfigured base URL fails loudly instead of
 * quietly sending cookies somewhere else.
 */
export class Client {
  constructor(private readonly baseUrl: string = '') {}

  protected get<T>(path: string): Promise<T> {
    return this.request('GET', path);
  }

  protected post<T>(path: string, body?: unknown): Promise<T> {
    return this.request('POST', path, body);
  }

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = {};
    if (body !== undefined) {
      headers['content-type'] = 'application/json';
    }
    // The double-submit half of the CSRF check. Reads do not need it, but
    // sending it always is simpler than deciding per call.
    const token = csrfToken();
    if (token) {
      headers[CSRF_HEADER] = token;
    }

    const response = await fetch(`${this.baseUrl}${path}`, {
      method,
      headers,
      credentials: 'same-origin',
      body: body === undefined ? undefined : JSON.stringify(body),
    });

    if (!response.ok) {
      let type = 'unknown';
      let message = `${method} ${path} failed with ${response.status}`;
      try {
        const parsed = (await response.json()) as ApiErrorBody;
        type = parsed.error?.type ?? type;
        message = parsed.error?.message ?? message;
      } catch {
        // A non-JSON error body is still an error; the status is what matters.
      }
      throw new ApiError(response.status, type, message);
    }

    if (response.status === 204) {
      return undefined as T;
    }
    return (await response.json()) as T;
  }

  me(): Promise<Me> {
    return this.request('GET', '/v1/me');
  }

  devLogin(email: string): Promise<{ user_id: string; email: string }> {
    return this.request('POST', '/v1/auth/dev-login', { email });
  }

  logout(): Promise<{ signed_out: boolean }> {
    return this.request('POST', '/v1/auth/logout');
  }

  account(accountId: string): Promise<Account> {
    return this.request('GET', `/v1/accounts/${accountId}`);
  }

  listKeys(accountId: string): Promise<{ data: ApiKey[] }> {
    return this.request('GET', `/v1/accounts/${accountId}/keys`);
  }

  createKey(accountId: string): Promise<MintedKey> {
    return this.request('POST', `/v1/accounts/${accountId}/keys`);
  }

  revokeKey(accountId: string, keyId: string): Promise<{ revoked: boolean }> {
    return this.request('POST', `/v1/accounts/${accountId}/keys/${keyId}/revoke`);
  }

  usage(accountId: string): Promise<UsageTotals> {
    return this.request('GET', `/v1/accounts/${accountId}/usage`);
  }

  invoices(accountId: string): Promise<{ data: Invoice[] }> {
    return this.request('GET', `/v1/accounts/${accountId}/invoices`);
  }
}

/** What the admin surface returns. Staff-only; see `AdminClient`. */
export interface AdminAccount extends Omit<Account, 'role'> {
  spend_cap_cents: number | null;
  created_at: string;
}

export interface InvoiceLine {
  metric: string;
  model_id: string;
  quantity: number;
  unit_micros: number;
  amount_micros: number;
}

export interface Invoice {
  id: number;
  account_id: string;
  period_start: string;
  period_end: string;
  price_version: number;
  status: string;
  total_micros: number;
  currency: string;
  lines: InvoiceLine[];
}

export interface AccountDetail {
  account: AdminAccount;
  keys: ApiKey[];
  usage: UsageTotals;
  invoices: Invoice[];
}

export interface PolicyStatus {
  version: number;
  keys: number;
  revoked: number;
  issued_at: string;
}

/**
 * The fleet view is allowed to be unavailable — an ops view must never become
 * a dependency of the thing it watches — so `available` is part of the
 * successful response rather than signalled by an error status.
 */
export interface Fleet {
  available: boolean;
  reason?: string;
  metrics?: Record<string, number>;
}

export interface AuditEntry {
  actor: string;
  action: string;
  target: string | null;
  ip: string | null;
}

/** A separate client for a separate origin, so admin calls cannot be made by
 *  accident from the customer portal's code. */
export class AdminClient extends Client {
  accounts(query = ''): Promise<{ data: AdminAccount[] }> {
    return this.get(`/admin/v1/accounts?query=${encodeURIComponent(query)}`);
  }

  accountDetail(accountId: string): Promise<AccountDetail> {
    return this.get(`/admin/v1/accounts/${accountId}`);
  }

  suspend(accountId: string): Promise<{ status: string }> {
    return this.post(`/admin/v1/accounts/${accountId}/suspend`);
  }

  unsuspend(accountId: string): Promise<{ status: string }> {
    return this.post(`/admin/v1/accounts/${accountId}/unsuspend`);
  }

  lookupKey(keyId: string): Promise<ApiKey> {
    return this.get(`/admin/v1/keys/${keyId}`);
  }

  revokeKeyById(keyId: string): Promise<{ revoked: boolean }> {
    return this.post(`/admin/v1/keys/${keyId}/revoke`);
  }

  rateNow(accountId: string): Promise<Invoice> {
    return this.post(`/admin/v1/accounts/${accountId}/rate`);
  }

  policy(): Promise<PolicyStatus> {
    return this.get('/admin/v1/policy');
  }

  fleet(): Promise<Fleet> {
    return this.get('/admin/v1/fleet');
  }

  audit(): Promise<{ data: AuditEntry[] }> {
    return this.get('/admin/v1/audit');
  }
}

export const client = new Client();
export const adminClient = new AdminClient();
